import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiUpload } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Minus, Trophy, ExternalLink, Upload } from "lucide-react";

export default function LiveMatch() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [fx, setFx] = useState(null);
  const [teams, setTeams] = useState([]);
  const [finishOpen, setFinishOpen] = useState(false);

  const load = useCallback(async () => {
    const [f, t] = await Promise.all([
      api.get(`/fixtures/${id}/live`),
      api.get("/teams"),
    ]);
    setFx(f.data);
    setTeams(t.data);
  }, [id]);

  useEffect(() => {
    load().catch(() => nav("/live"));
    const timer = setInterval(() => load().catch(() => {}), 4000);
    return () => clearInterval(timer);
  }, [load, nav]);

  if (!fx) return <div className="text-gray-500">Loading live match…</div>;
  const home = teams.find((t) => t.id === fx.home_team_id);
  const away = teams.find((t) => t.id === fx.away_team_id);
  const isLive = fx.status === "live";
  const isCompleted = fx.status === "completed";

  const bump = async (side, dir = 1) => {
    const key = side === "home" ? "home_score" : "away_score";
    const next = Math.max(0, (fx[key] || 0) + dir);
    try {
      const { data } = await api.put(`/fixtures/${id}/live`, { [key]: next });
      setFx(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const finish = async () => {
    // Iter39 · Enforce HLL Warfare scores client-side too (5-0 / 4-1 / 3-2 only)
    const pair = [fx.home_score || 0, fx.away_score || 0].sort((a, b) => b - a).join("-");
    if (!["5-0", "4-1", "3-2"].includes(pair)) {
      toast.error(`Invalid HLL Warfare score (${fx.home_score || 0}-${fx.away_score || 0}). Must be 5-0, 4-1, or 3-2.`);
      return;
    }
    try {
      await api.post(`/fixtures/${id}/result`, {
        home_score: fx.home_score || 0,
        away_score: fx.away_score || 0,
      });
      toast.success("Match complete. Standings updated.");
      setFinishOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="live-match-root">
      {/* Scoreboard */}
      <div className="border border-red-500/50 bg-black/80 backdrop-blur p-6 lg:p-10 relative overflow-hidden">
        <div className="absolute top-4 left-4 flex items-center gap-2">
          {isLive && (
            <span className="bg-red-600 text-white text-[10px] font-mono font-bold tracking-widest px-2 py-0.5 flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> LIVE
            </span>
          )}
          {isCompleted && (
            <span className="bg-[#232330] text-gray-400 text-[10px] font-mono font-bold tracking-widest px-2 py-0.5">FULL TIME</span>
          )}
          <span className="text-[10px] text-cyan-400 font-mono tracking-widest">{fx.game}{fx.map ? ` · ${fx.map}` : ""}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-6 pt-6">
          <div className="text-center md:text-right">
            <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase mb-2">HOME</div>
            <div className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tighter" style={{ color: home?.logo_color }}>
              {home?.name}
            </div>
            {isAdmin && isLive && (
              <div className="mt-3 flex justify-center md:justify-end gap-2">
                <Button size="sm" onClick={() => bump("home", -1)} data-testid="home-minus"
                        className="rounded-none bg-transparent border border-[#232330] hover:border-red-500 text-gray-400 hover:text-red-500">
                  <Minus className="w-4 h-4" />
                </Button>
                <Button size="sm" onClick={() => bump("home", +1)} data-testid="home-plus"
                        className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-bold">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          <div className="text-center">
            <div className="font-heading font-black text-6xl md:text-8xl leading-none">
              <span className="text-white">{fx.home_score ?? 0}</span>
              <span className="text-gray-700 mx-3 md:mx-5">·</span>
              <span className="text-white">{fx.away_score ?? 0}</span>
            </div>
            <div className="text-xs font-mono text-red-500 tracking-widest uppercase mt-3">
              {isLive ? "IN PLAY" : isCompleted ? "FINAL" : "PENDING"}
            </div>
          </div>

          <div className="text-center md:text-left">
            <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase mb-2">AWAY</div>
            <div className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tighter" style={{ color: away?.logo_color }}>
              {away?.name}
            </div>
            {isAdmin && isLive && (
              <div className="mt-3 flex justify-center md:justify-start gap-2">
                <Button size="sm" onClick={() => bump("away", -1)} data-testid="away-minus"
                        className="rounded-none bg-transparent border border-[#232330] hover:border-red-500 text-gray-400 hover:text-red-500">
                  <Minus className="w-4 h-4" />
                </Button>
                <Button size="sm" onClick={() => bump("away", +1)} data-testid="away-plus"
                        className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white font-bold">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {isAdmin && isLive && (
          <div className="mt-8 flex justify-center">
            <Dialog open={finishOpen} onOpenChange={setFinishOpen}>
              <DialogTrigger asChild>
                <Button data-testid="finish-match"
                        className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold">
                  <Trophy className="w-4 h-4 mr-2" /> End match & lock result
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
                <DialogHeader>
                  <DialogTitle className="font-heading uppercase tracking-tight">Lock final score?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-gray-300">
                  Final: <span className="font-mono text-white font-bold">{home?.tag} {fx.home_score ?? 0} — {fx.away_score ?? 0} {away?.tag}</span>.
                  This updates standings.
                </p>
                <Button onClick={finish} data-testid="confirm-finish"
                        className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold">
                  Publish full-time
                </Button>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Iter45 · Stats CSV upload (visible once match is completed) */}
      {isCompleted && (
        <div
          data-testid="live-match-stats-upload"
          className="border border-cyan-400/40 bg-cyan-400/5 p-5 space-y-3"
        >
          <div className="flex items-center gap-3">
            <Upload className="w-4 h-4 text-cyan-400" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400">Match stats</div>
              <div className="text-sm text-gray-300 mt-1">
                Upload the server export (<span className="font-mono text-cyan-400">handle,kills,deaths,assists</span>) so it feeds the Players page and top-fraggers board.
              </div>
              {fx.stats_imported && (
                <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-emerald-400">
                  ✓ Uploaded {fx.stats_imported_at ? new Date(fx.stats_imported_at).toLocaleString() : ""} · {fx.stats_updated_count || 0} players
                </div>
              )}
            </div>
            <label
              data-testid="live-match-stats-input"
              className="ml-auto cursor-pointer text-[10px] font-mono uppercase tracking-widest text-black bg-cyan-400 hover:bg-cyan-300 px-4 py-2 flex items-center gap-2 font-bold"
            >
              <Upload className="w-3.5 h-3.5" /> {fx.stats_imported ? "Replace" : "Upload CSV"}
              <input type="file" accept=".csv,text/csv" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; e.target.value = "";
                  if (!f) return;
                  try {
                    const fd = new FormData(); fd.append("file", f);
                    const { data } = await apiUpload(`/fixtures/${id}/stats-upload`, fd);
                    const unmatched = data.unmatched_handles || [];
                    let msg = `Stats saved · ${data.updated_count} player${data.updated_count === 1 ? "" : "s"} updated`;
                    if (unmatched.length) {
                      msg += ` · ${unmatched.length} unmatched (${unmatched.slice(0,3).join(", ")}${unmatched.length>3?"…":""})`;
                      toast.warning(msg);
                    } else {
                      toast.success(msg);
                    }
                    load();
                  } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                }} />
            </label>
          </div>
        </div>
      )}

      {/* Attached stream */}
      {fx.stream_url ? (
        <a
          href={fx.stream_url}
          target="_blank"
          rel="noreferrer"
          data-testid="live-match-stream-link"
          className="block border border-[#B084FF]/60 bg-[#B084FF]/5 hover:bg-[#B084FF]/10 p-5 transition-colors"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] font-mono tracking-widest text-[#B084FF] uppercase">
                Streamed by {fx.stream_streamer_handle || "match cast"}
              </div>
              <div className="font-heading text-2xl font-black uppercase tracking-tight mt-1 truncate">
                Watch this match live
              </div>
              <div className="text-xs text-gray-400 font-mono mt-1 truncate">{fx.stream_url}</div>
            </div>
            <div className="rounded-none bg-[#B084FF] hover:bg-purple-400 text-black px-5 py-3 uppercase tracking-widest text-xs font-bold flex items-center gap-2">
              <ExternalLink className="w-4 h-4" /> Jump in
            </div>
          </div>
        </a>
      ) : (
        <div className="border border-dashed border-[#232330] p-6 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
          No streamer attached to this match yet. Owners, reps and verified streamers can attach one from the Fixtures page.
        </div>
      )}
    </div>
  );
}
